import React, { useState } from 'react';
import { X, Upload, Download, ClipboardList, Loader2, AlertCircle, Info } from 'lucide-react';
import { Player } from '../types';
import { teamService } from '../services/teamService';

interface ImportPlayersModalProps {
  teamId: string;
  onClose: () => void;
  onPlayersImported: (players: Player[]) => void;
}

export const ImportPlayersModal: React.FC<ImportPlayersModalProps> = ({
  teamId,
  onClose,
  onPlayersImported,
}) => {
  const [csvData, setCsvData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');

  const generateCSVTemplate = () => {
    const headers = 'firstName,lastName,dateOfBirth,position,fanNumber';
    const exampleRows = [
      'John,Smith,2010-05-15,Forward,FA123456',
      'Emma,Johnson,2011-03-22,Midfield,FA789012',
      'Michael,Brown,2010-08-10,Defender,',
      'Sarah,Wilson,2011-01-18,Goalkeeper,FA345678',
      'David,Taylor,2010-11-05,Winger,FA901234',
    ];
    return [headers, ...exampleRows].join('\n');
  };

  const downloadTemplate = () => {
    const csvContent = generateCSVTemplate();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'player-import-template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const loadTemplate = () => {
    setCsvData(generateCSVTemplate());
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setCsvData(text);
      };
      reader.readAsText(file);
    }
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      setServerError('Please provide CSV data');
      return;
    }
    setIsLoading(true);
    setServerError('');
    try {
      const importedPlayers = await teamService.importPlayers(teamId, csvData);
      onPlayersImported(importedPlayers);
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || error.message || 'Failed to import players');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
              <Upload className="w-5 h-5 text-green-700" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Import Players</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 space-y-2">
                <p className="font-medium">CSV Format</p>
                <p>Upload a CSV file or paste data with these columns:</p>
                <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                  <li>firstName, lastName (required)</li>
                  <li>dateOfBirth — YYYY-MM-DD (required)</li>
                  <li>position — Goalkeeper, Defender, Midfield, Winger, Forward (required)</li>
                  <li>fanNumber (optional)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Template Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
            <button
              type="button"
              onClick={loadTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
            >
              <ClipboardList className="w-4 h-4" />
              Load Template
            </button>
          </div>

          {/* CSV Example */}
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 overflow-x-auto">
{`firstName,lastName,dateOfBirth,position,fanNumber
John,Smith,2010-05-15,Forward,FA123456
Emma,Johnson,2011-03-22,Midfield,FA789012`}
          </pre>

          {/* File Upload */}
          <div>
            <label htmlFor="csvFile" className="block text-sm font-medium text-gray-700 mb-1.5">
              Upload CSV File
            </label>
            <input
              id="csvFile"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50 file:cursor-pointer file:min-h-[36px] cursor-pointer border border-dashed border-gray-300 rounded-lg p-2 min-h-[44px]"
            />
          </div>

          {/* Paste CSV */}
          <div>
            <label htmlFor="csvData" className="block text-sm font-medium text-gray-700 mb-1.5">
              Or Paste CSV Data
            </label>
            <textarea
              id="csvData"
              value={csvData}
              onChange={(e) => setCsvData(e.target.value)}
              rows={6}
              placeholder={`firstName,lastName,dateOfBirth,position,fanNumber\nJohn,Smith,2010-05-15,Forward,FA123456`}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-vertical"
            />
          </div>

          {/* Error */}
          {serverError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2.5 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={isLoading || !csvData.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Import Players
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
